import React from 'react';
import { Link, useParams } from '@tanstack/react-router';
import Header from './Home/components/Header';
import { 
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import articles from '../articles';
import { useSEO } from '../hooks/useSEO';

const getTagColor = () => 'bg-accent/10 text-accent';

export default function SupportArticle() {
  const { articleId } = useParams({ from: '/docs/support/$articleId' });
  const article = articles.find(a => a.id === articleId);

  useSEO({
    title: article ? (article.seoTitle || `${article.title} – Compress GLB Docs`) : 'Article not found – Compress GLB Docs',
    description: article?.seoDescription || article?.description,
    canonical: `/docs/support/${articleId}`,
    ogImage: '/images/og/docs.png',
    robots: article ? 'index, follow' : 'noindex, follow',
    ogTitle: article?.title,
    ogDescription: article?.description,
    structuredData: article && {
      '@context': 'https://schema.org',
      '@type': 'TechArticle',
      headline: article.title,
      description: article.description,
      url: `https://compress-glb.com/docs/support/${articleId}/`,
      dateModified: article.lastUpdated,
      publisher: { '@type': 'Organization', name: 'Compress GLB' },
    },
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-foreground mb-4">Article Not Found</h1>
            <p className="text-muted-foreground mb-8">The article you're looking for doesn't exist.</p>
            <Link
              to="/docs/support"
              className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary-hover transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Support
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <nav className="flex items-center gap-1 text-xs">
            <Link to="/docs" className="text-muted hover:text-accent">Docs</Link>
            <span className="text-muted">/</span>
            <Link to="/docs/support" className="text-muted hover:text-accent">Support</Link>
            <span className="text-muted">/</span>
            <Link to={`/docs/support#${article.category}`} className="text-muted hover:text-accent capitalize">{article.category}</Link>
            <span className="text-muted">/</span>
            <span className="text-muted-foreground">{article.title}</span>
          </nav>
          
          <Link
            to="/docs/support"
            className="inline-flex items-center text-xs text-muted hover:text-foreground transition-colors"
          >
            <ArrowLeftIcon className="w-3 h-3 mr-1" />
            Back
          </Link>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">{article.title}</h1>
          
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">{article.description}</p>
            
            {article.tags && (
              <div className="flex flex-wrap justify-end gap-1 ml-4">
                {article.tags.map(tag => (
                  <span key={tag} className={`px-2 py-1 rounded text-xs ${getTagColor(tag)}`}>
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
          
          <span className="text-xs text-muted">Updated {article.lastUpdated}</span>
        </div>

        <div className="bg-background-soft rounded-lg p-8">
          {article.content()}
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-semibold text-foreground mb-6">Related Articles</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {articles
              .filter(a => a.category === article.category && a.id !== article.id)
              .slice(0, 4)
              .map(relatedArticle => (
                <Link
                  key={relatedArticle.id}
                  to={`/docs/support/${relatedArticle.id}`}
                  className="bg-background-soft border border-border rounded-lg p-4 hover:border-primary transition-colors group"
                >
                  <h3 className="text-lg font-medium text-foreground group-hover:text-accent mb-2">
                    {relatedArticle.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {relatedArticle.description}
                  </p>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
} 